<template>
  <div v-if="isOpen" class="fixed inset-0 z-[100]">
    <div class="absolute inset-0 bg-black/40" @click="onClose"></div>
    <div class="absolute right-0 top-0 h-full w-full max-w-5xl bg-white shadow-2xl border-l border-gray-200 flex">
      <!-- Sidebar (TOC) -->
      <aside class="w-80 border-r border-gray-200 p-4 hidden md:flex flex-col">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-base font-semibold text-gray-900">Guide de la plateforme</h3>
          <span class="text-xs text-gray-500">{{ version }}</span>
        </div>
        <input v-model="q" type="text" class="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Rechercher… (modules, actions)" />
        <div class="mt-2 flex items-center justify-between">
          <span class="text-xs text-gray-600">{{ tocModules.length }} module{{ tocModules.length > 1 ? 's' : '' }}</span>
          <button @click="toggleExpandAll" class="text-xs text-primary-700 hover:text-primary-900 hover:underline flex items-center gap-1">
            <span>{{ allTocExpanded ? 'Réduire tout' : 'Tout développer' }}</span>
            <span>{{ allTocExpanded ? '▾' : '▸' }}</span>
          </button>
        </div>
        <div class="mt-3 overflow-auto">
          <div v-for="m in tocModules" :key="m.key" class="mb-2">
            <div class="flex items-center justify-between">
              <button class="flex-1 text-left text-sm font-medium text-gray-800 hover:text-primary-700" @click="scrollTo(m.key)">{{ m.label }}</button>
              <button class="ml-2 text-xs text-gray-600 hover:text-primary-700" @click="tocToggle(m.key)">
                <span v-if="tocIsExpanded(m.key)">▾</span>
                <span v-else>▸</span>
              </button>
            </div>
            <ul v-if="tocIsExpanded(m.key)" class="mt-1 ml-3 space-y-1">
              <li v-for="s in m.sections" :key="s.id">
                <button class="text-xs text-gray-600 hover:text-primary-700" @click="scrollTo(s.id)">{{ s.title }}</button>
              </li>
            </ul>
          </div>
        </div>
      </aside>

      <!-- Content -->
      <section class="flex-1 p-4 overflow-auto">
        <div class="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Bienvenue, {{ userDisplayName }}</h2>
            <p class="text-sm text-gray-600">Ce guide adapte son contenu à vos rôles et permissions.</p>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="canEdit"
              @click="isEditMode = !isEditMode"
              class="px-3 py-2 rounded text-sm transition-colors"
              :class="isEditMode 
                ? 'bg-primary-600 text-white hover:bg-primary-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
            >
              <span class="flex items-center space-x-1">
                <svg v-if="!isEditMode" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{{ isEditMode ? 'Annuler' : 'Éditer' }}</span>
              </span>
            </button>
            <button class="px-3 py-2 bg-gray-100 rounded text-sm" @click="onClose">Fermer</button>
          </div>
        </div>

        <!-- Mode Édition -->
        <GuideEditor
          v-if="isEditMode"
          :version="version"
          :modules="modules"
          @close="isEditMode = false"
          @save="handleGuideSave"
        />

        <!-- Mode Consultation -->
        <template v-else>
          <!-- Onglets Texte / Vidéo -->
          <div class="mb-4 border-b border-gray-200">
            <nav class="flex space-x-8">
              <button
                @click="activeMode = 'text'"
                class="py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
                :class="activeMode === 'text' 
                  ? 'border-primary-500 text-primary-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
              >
                <span class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Texte</span>
                </span>
              </button>
              <button
                @click="activeMode = 'video'"
                class="py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
                :class="activeMode === 'video' 
                  ? 'border-primary-500 text-primary-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
              >
                <span class="flex items-center space-x-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Vidéo</span>
                  <span v-if="hasVideoContent" class="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                    {{ videoSectionsCount }}
                  </span>
                </span>
              </button>
            </nav>
          </div>

        <!-- Un module par ligne pour meilleure lisibilité -->
        <div class="mt-4 grid grid-cols-1 gap-6">
          <article v-for="m in filteredModules" :key="m.key" :id="m.key" class="border border-gray-200 rounded-lg overflow-hidden">
            <header class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 class="text-sm font-semibold text-gray-900">{{ m.label }}</h3>
              <span class="text-[11px] text-gray-500">Module</span>
            </header>
            <div class="p-4 space-y-3">
              <p class="text-sm text-gray-700" v-if="m.description">{{ m.description }}</p>
              <div class="mt-2">
                <button class="text-xs text-primary-700 hover:underline" @click="toggleExpand(m.key)">{{ isExpanded(m.key) ? 'Réduire' : 'Voir plus' }}</button>
              </div>
              <!-- Mode Texte -->
              <template v-if="activeMode === 'text'">
                <div v-for="s in visibleSections(m)" :key="s.id" :id="s.id" class="border-t border-gray-100 pt-3">
                  <h4 class="text-sm font-medium text-gray-800">{{ s.title }}</h4>
                  <p v-if="s.text" class="text-xs text-gray-600 mt-1">{{ s.text }}</p>
                  <!-- Steps (guide pas-à-pas) -->
                  <ol v-if="s.steps?.length" class="mt-2 list-decimal list-inside space-y-1 text-xs text-gray-700">
                    <li v-for="(step, idx) in s.steps" :key="s.id + '-step-' + idx">{{ step }}</li>
                  </ol>
                  <!-- Actions contextuelles -->
                  <div v-if="s.actions?.length" class="mt-2 flex flex-wrap gap-2">
                    <button
                      v-for="a in s.actions"
                      :key="s.id + '-act-' + a.label"
                      class="px-2 py-1 text-xs bg-primary-50 text-primary-700 border border-primary-200 rounded hover:bg-primary-100"
                      @click="go(a.to)"
                    >
                      {{ a.label }}
                    </button>
                  </div>
                  <!-- Image (affichée uniquement si mediaType est 'image') -->
                  <div v-if="s.mediaType === 'image' && s.imageName" class="mt-2 rounded border border-dashed border-gray-300 bg-gray-50 p-2">
                    <img 
                      :src="getImageUrl(s.imageName)" 
                      :alt="`Illustration: ${s.title}`" 
                      class="w-full rounded" 
                      @error="handleImageError($event, s.imageName)"
                      loading="lazy"
                      crossorigin="anonymous"
                    />
                  </div>
                  
                  <!-- Vidéo (affichée uniquement si mediaType est 'video' en mode texte) -->
                  <div v-if="s.mediaType === 'video' && activeMode === 'text'" class="mt-2 rounded-lg overflow-hidden border border-gray-300 bg-gray-50">
                    <div class="aspect-video w-full">
                      <iframe
                        v-if="getVideoEmbedUrl(s)"
                        :src="getVideoEmbedUrl(s)"
                        class="w-full h-full"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                        :title="`Guide vidéo: ${s.title}`"
                      ></iframe>
                      <div v-else class="w-full h-full flex items-center justify-center bg-gray-100">
                        <div class="text-center p-4">
                          <svg class="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <p class="text-sm text-gray-600 mb-2">Vidéo non disponible</p>
                          <a v-if="s.videoUrl" :href="s.videoUrl" target="_blank" rel="noopener" class="text-xs text-primary-700 hover:underline">
                            Ouvrir la vidéo dans un nouvel onglet
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="s.links?.length" class="mt-2 flex flex-wrap gap-2">
                    <a v-for="l in s.links" :key="l.href" :href="l.href" class="text-xs text-primary-700 hover:underline" target="_blank" rel="noopener">{{ l.label }}</a>
                  </div>
                </div>
              </template>

              <!-- Mode Vidéo -->
              <template v-else-if="activeMode === 'video'">
                <div v-for="s in visibleVideoSections(m)" :key="s.id" :id="s.id + '-video'" class="border-t border-gray-100 pt-3">
                  <h4 class="text-sm font-medium text-gray-800">{{ s.title }}</h4>
                  
                  <!-- Vidéo embed (affichée uniquement si mediaType est 'video') -->
                  <div v-if="s.mediaType === 'video'" class="mt-3 rounded-lg overflow-hidden border border-gray-300 bg-gray-50">
                    <div class="aspect-video w-full">
                      <iframe
                        v-if="getVideoEmbedUrl(s)"
                        :src="getVideoEmbedUrl(s)"
                        class="w-full h-full"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                        :title="`Guide vidéo: ${s.title}`"
                      ></iframe>
                      <div v-else class="w-full h-full flex items-center justify-center bg-gray-100">
                        <div class="text-center p-4">
                          <svg class="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <p class="text-sm text-gray-600 mb-2">Vidéo non disponible</p>
                          <a v-if="s.videoUrl" :href="s.videoUrl" target="_blank" rel="noopener" class="text-xs text-primary-700 hover:underline">
                            Ouvrir la vidéo dans un nouvel onglet
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Description texte optionnelle -->
                  <p v-if="s.text" class="text-xs text-gray-600 mt-3">{{ s.text }}</p>

                  <!-- Lien vers la vidéo originale -->
                  <div v-if="s.videoUrl" class="mt-2">
                    <a :href="s.videoUrl" target="_blank" rel="noopener" class="text-xs text-primary-700 hover:underline flex items-center space-x-1">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span>Ouvrir sur {{ getVideoPlatformName(s.videoPlatform) }}</span>
                    </a>
                  </div>

                  <!-- Message si pas de vidéo -->
                  <div v-if="s.mediaType !== 'video'" class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p class="text-xs text-yellow-800">Aucune vidéo disponible pour cette section.</p>
                  </div>
                </div>
              </template>
            </div>
          </article>
        </div>
        </template>
      </section>
    </div>
  </div>
  
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useMenuPermissions } from '@/composables/useMenuPermissions'
import { getGuideContent, buildDefaultModuleContent, type GuideModuleContent } from '@/constants/guide.content'
// Import dynamique pour éviter les erreurs TypeScript
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import GuideEditor from './GuideEditor.vue'
import guidesService from '@/services/guides.service'
import { useToast } from 'vue-toastification'

interface Props {
  isOpen: boolean
  version?: string
}

const props = withDefaults(defineProps<Props>(), { version: 'v1' })
const emit = defineEmits(['close'])

const onClose = () => emit('close')
const q = ref('')

const auth = useAuthStore()
const { visibleMenuItems } = useMenuPermissions()
const toast = useToast()

const userDisplayName = computed(() => auth.user?.fullName || auth.user?.nomComplet || `${auth.user?.prenom || ''} ${auth.user?.nom || ''}`.trim() || 'Utilisateur')

// Mode actif (texte ou vidéo)
const activeMode = ref<'text' | 'video'>('text')

// Mode édition
const isEditMode = ref(false)

// Vérifier si l'utilisateur peut éditer les guides
const canEdit = computed(() => {
  const user = auth.user
  if (!user) return false
  // Vérifier les permissions pour l'édition des guides
  const hasEditPermission = user.permissions?.includes('guide:update') || 
                           user.permissions?.includes('guide:manage_content')
  // Seuls les utilisateurs internes avec les bonnes permissions peuvent éditer
  return user.userType === 'internal' && hasEditPermission
})

// Gérer la sauvegarde des guides
const handleGuideSave = async () => {
  try {
    // Recharger les guides depuis l'API
    await loadGuidesFromApi()
    toast.success('Guide sauvegardé avec succès')
    isEditMode.value = false
  } catch (error) {
    console.error('Erreur lors de la sauvegarde:', error)
    toast.error('Erreur lors de la sauvegarde du guide')
  }
}

// Stocker les guides chargés depuis l'API
const guidesFromApi = ref<Guide[]>([])
const loadingGuides = ref(false)

// Charger les guides depuis l'API
const loadGuidesFromApi = async () => {
  loadingGuides.value = true
  try {
    const guides = await guidesService.findByVersion(props.version)
    console.log('Guides chargés depuis l\'API:', guides)
    
    // Vérifier que guides est un tableau
    if (!Array.isArray(guides)) {
      console.warn('Les guides ne sont pas un tableau:', guides)
      guidesFromApi.value = []
      return
    }
    
    // Les guides sont uniquement par défaut (pour toute la plateforme), pas de priorité à gérer
    guidesFromApi.value = guides
    console.log('Guides organisés depuis l\'API:', guidesFromApi.value)
  } catch (error) {
    console.warn('Impossible de charger les guides depuis l\'API:', error)
    guidesFromApi.value = []
  } finally {
    loadingGuides.value = false
  }
}

// Charger les guides quand le modal s'ouvre
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    loadGuidesFromApi()
  }
}, { immediate: true })

// Utiliser les types du service pour garantir la cohérence avec le backend
import type { GuideSection, Guide } from '@/services/guides.service'

type GuideModule = { 
  _id?: string // ID du guide si déjà sauvegardé
  key: string
  label: string
  description?: string
  order?: number
  sections: GuideSection[] // GuideSection inclut déjà actions
  tags?: string[]
  metadata?: Record<string, any>
  organizationId?: string
}

// Construire la liste des modules visibles depuis le menu (RBAC déjà appliqué)
const versionContent = getGuideContent(props.version)

// Créer un Map des guides de l'API par clé pour un accès rapide
const apiGuidesMap = computed(() => {
  const map = new Map<string, Guide>()
  for (const guide of guidesFromApi.value) {
    map.set(guide.key, guide)
  }
  return map
})

const modules = computed<GuideModule[]>(() => {
  const items = Array.isArray(visibleMenuItems.value) ? visibleMenuItems.value : []
  const normalizeBaseKey = (mi: any) => {
    const path = String(mi.to || mi.key || '').trim()
    if (path.startsWith('/')) {
      const seg = path.split('/').find(Boolean)
      return (seg || 'module')
    }
    return String(mi.key || mi.title || mi.name || 'module').toLowerCase()
  }

  const rbacModules = items.map((mi: any) => {
    const baseKey = normalizeBaseKey(mi)
    const label = mi.title || mi.name || mi.key
    
    // Priorité 1: Guide depuis l'API
    const apiGuide = apiGuidesMap.value.get(baseKey)
    if (apiGuide) {
      return {
        _id: apiGuide._id,
        key: apiGuide.key,
        label: apiGuide.label,
        description: apiGuide.description,
        order: apiGuide.order,
        sections: apiGuide.sections,
        tags: apiGuide.tags,
        metadata: apiGuide.metadata,
        organizationId: apiGuide.organizationId
      } as GuideModule
    }
    
    // Priorité 2: Contenu statique de la version
    const fromVersion: GuideModuleContent | undefined = versionContent[baseKey]
    if (fromVersion) {
      return fromVersion as GuideModule
    }
    
    // Priorité 3: Contenu par défaut généré
    return buildDefaultModuleContent(baseKey, label, mi.to) as GuideModule
  })

  // Modules de documentation toujours visibles (indépendamment du RBAC)
  // Priorité: API > Version statique > Défaut
  const authModuleFromApi = apiGuidesMap.value.get('auth')
  const authModuleFromVersion = versionContent['auth']
  const authModule: GuideModule = authModuleFromApi ? {
    _id: authModuleFromApi._id,
    key: authModuleFromApi.key,
    label: authModuleFromApi.label,
    description: authModuleFromApi.description,
    order: authModuleFromApi.order,
    sections: authModuleFromApi.sections,
    tags: authModuleFromApi.tags,
    metadata: authModuleFromApi.metadata,
    organizationId: authModuleFromApi.organizationId
  } as GuideModule : (authModuleFromVersion || {
    key: 'auth',
    label: 'Authentification & Accès',
    description: 'Connexion, récupération et réinitialisation du mot de passe, sécurité et bonnes pratiques.',
    sections: [
      { id: 'auth-login', title: 'Login', text: 'Saisir email et mot de passe. Messages d’erreurs courants et résolution.', imageName: 'auth-login.png', steps: [
        'Aller sur la page de connexion.',
        'Entrer email et mot de passe.',
        'Valider et résoudre les messages d’erreurs si besoin.'
      ], actions: [ { label: 'Ouvrir Login', to: '/login' } ] },
      { id: 'auth-forgot', title: 'Mot de passe oublié', text: 'Envoi d’un email de réinitialisation, liens et délais de validité.', imageName: 'auth-forgot.png', steps: [
        'Cliquer sur “Mot de passe oublié”.',
        'Saisir l’email associé au compte.',
        'Vérifier le mail reçu et suivre le lien avant expiration.'
      ], actions: [ { label: 'Ouvrir Mot de passe oublié', to: '/forgot-password' } ] },
      { id: 'auth-reset', title: 'Réinitialisation', text: 'Définition d’un nouveau mot de passe; exigences de complexité.', imageName: 'auth-reset.png', steps: [
        'Ouvrir le lien de réinitialisation reçu.',
        'Saisir et confirmer le nouveau mot de passe conforme.',
        'Se reconnecter avec le nouveau mot de passe.'
      ], actions: [ { label: 'Aller à Réinitialisation', to: '/reset-password' } ] },
      { id: 'auth-security', title: 'Sécurité & 2FA', text: 'Rotation, expiration, 2FA et politique de sessions.', imageName: 'auth-security.png', steps: [
        'Activer la 2FA si disponible.',
        'Respecter la rotation/expiration du mot de passe.',
        'Fermer les sessions inactives ou non reconnues.'
      ] },
    ]
  } as GuideModule)

  // Modules de documentation toujours visibles (indépendamment du RBAC)
  // Priorité: API > Version statique
  const getModuleFromApiOrStatic = (key: string): GuideModule | undefined => {
    const apiGuide = apiGuidesMap.value.get(key)
    if (apiGuide) {
      return {
        _id: apiGuide._id,
        key: apiGuide.key,
        label: apiGuide.label,
        description: apiGuide.description,
        order: apiGuide.order,
        sections: apiGuide.sections,
        tags: apiGuide.tags,
        metadata: apiGuide.metadata,
        organizationId: apiGuide.organizationId
      } as GuideModule
    }
    return versionContent[key] as GuideModule | undefined
  }
  
  const introModule = getModuleFromApiOrStatic('introduction')
  const vueEnsembleModule = getModuleFromApiOrStatic('vue-ensemble')
  const securiteModule = getModuleFromApiOrStatic('securite')
  const outilsModule = getModuleFromApiOrStatic('outils')

  // Ordre recommandé du sommaire (incluant les modules de documentation)
  const order = ['introduction','vue-ensemble','auth','dashboards','employes','absences','paie','presence','documents','rapports','utilisateurs','configuration','securite','outils','remuneration','notes-frais','syndicats','equipements','departements','postes','organizations','notifications']
  const ordered = rbacModules.sort((a, b) => {
    const idxA = order.indexOf(a.key)
    const idxB = order.indexOf(b.key)
    if (idxA === -1 && idxB === -1) return 0
    if (idxA === -1) return 1
    if (idxB === -1) return -1
    return idxA - idxB
  })
  
  // Construire la liste finale avec les modules de documentation en premier
  const finalModules: GuideModule[] = []
  if (introModule) finalModules.push(introModule as GuideModule)
  if (vueEnsembleModule) finalModules.push(vueEnsembleModule as GuideModule)
  if (authModule) finalModules.push(authModule as GuideModule)
  finalModules.push(...ordered)
  if (securiteModule) {
    const idx = finalModules.findIndex(m => m.key === 'securite')
    if (idx === -1) finalModules.push(securiteModule as GuideModule)
    else finalModules[idx] = securiteModule as GuideModule
  }
  if (outilsModule) {
    const idx = finalModules.findIndex(m => m.key === 'outils')
    if (idx === -1) finalModules.push(outilsModule as GuideModule)
    else finalModules[idx] = outilsModule as GuideModule
  }
  
  return finalModules
})

const filteredModules = computed(() => {
  const term = q.value.trim().toLowerCase()
  if (!term) return modules.value
  return modules.value.map(m => ({
    ...m,
    sections: m.sections.filter(s => (m.label + ' ' + s.title + ' ' + (s.text || '')).toLowerCase().includes(term))
  })).filter(m => m.sections.length > 0 || m.label.toLowerCase().includes(term))
})

// Gestion "Voir plus" par module
const expanded = ref<Set<string>>(new Set())
const toggleExpand = (key: string) => {
  const s = new Set(expanded.value)
  if (s.has(key)) s.delete(key); else s.add(key)
  expanded.value = s
}
const isExpanded = (key: string) => expanded.value.has(key)
const visibleSections = (m: GuideModule) => isExpanded(m.key) ? m.sections : m.sections.slice(0, 2)

// Sections visibles en mode vidéo (uniquement celles qui ont mediaType === 'video')
const visibleVideoSections = (m: GuideModule) => {
  const sections = m.sections.filter(s => s.mediaType === 'video')
  return isExpanded(m.key) ? sections : sections.slice(0, 2)
}

// Vérifier si une section a une vidéo (pour compatibilité)
const hasVideo = (section: GuideSection): boolean => {
  return section.mediaType === 'video' || !!(section.videoUrl || section.videoId)
}

// Compter le nombre total de sections avec vidéo
const videoSectionsCount = computed(() => {
  return modules.value.reduce((count, m) => {
    return count + m.sections.filter(s => hasVideo(s)).length
  }, 0)
})

// Vérifier s'il y a du contenu vidéo
const hasVideoContent = computed(() => videoSectionsCount.value > 0)

// Obtenir l'URL d'une image de guide depuis l'API
const getImageUrl = (imageName: string): string => {
  if (!imageName) return ''
  return guidesService.getImageUrl(props.version, imageName)
}

// Gérer les erreurs de chargement d'image
const handleImageError = (event: Event, imageName: string) => {
  const img = event.target as HTMLImageElement
  console.warn(`Impossible de charger l'image: ${imageName}`)
  // Optionnel: masquer l'image en cas d'erreur
  img.style.display = 'none'
}

// Extraire l'ID YouTube depuis une URL
const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /[?&]v=([^"&?/\s]{11})/,
    /youtu\.be\/([^"&?/\s]{11})/,
    /youtube\.com\/embed\/([^"&?/\s]{11})/
  ]
  
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match?.[1]) {
      return match[1]
    }
  }
  return null
}

// Extraire l'ID Vimeo depuis une URL
const extractVimeoId = (url: string): string | null => {
  const match = url.match(/vimeo\.com\/(\d+)/)
  return match?.[1] || null
}

// Extraire l'ID Dailymotion depuis une URL
const extractDailymotionId = (url: string): string | null => {
  const match = url.match(/dailymotion\.com\/(?:video|embed)\/([^"&?/\s]+)/)
  return match?.[1] || null
}

// Construire l'URL d'embed selon la plateforme
const buildEmbedUrl = (platform: string, videoId: string): string => {
  const urls: Record<string, string> = {
    youtube: `https://www.youtube.com/embed/${videoId}`,
    vimeo: `https://player.vimeo.com/video/${videoId}`,
    dailymotion: `https://www.dailymotion.com/embed/video/${videoId}`
  }
  return urls[platform] || ''
}

// Extraire l'ID depuis l'URL si nécessaire
const extractVideoIdFromUrl = (url: string): { id: string; platform: string } | null => {
  const youtubeId = extractYouTubeId(url)
  if (youtubeId) return { id: youtubeId, platform: 'youtube' }

  const vimeoId = extractVimeoId(url)
  if (vimeoId) return { id: vimeoId, platform: 'vimeo' }

  const dailymotionId = extractDailymotionId(url)
  if (dailymotionId) return { id: dailymotionId, platform: 'dailymotion' }

  return null
}

// Générer l'URL d'embed pour la vidéo
const getVideoEmbedUrl = (section: GuideSection): string | undefined => {
  if (!section.videoId && !section.videoUrl) return undefined

  // Si on a déjà un ID et une plateforme, construire l'URL directement
  if (section.videoId && section.videoPlatform) {
    return buildEmbedUrl(section.videoPlatform, section.videoId)
  }

  // Si on a une URL mais pas d'ID, essayer d'extraire l'ID depuis l'URL
  if (section.videoUrl) {
    const extracted = extractVideoIdFromUrl(section.videoUrl)
    if (extracted) {
      return buildEmbedUrl(extracted.platform, extracted.id)
    }
  }

  return undefined
}

// Obtenir le nom de la plateforme vidéo
const getVideoPlatformName = (platform?: string): string => {
  const names: Record<string, string> = {
    youtube: 'YouTube',
    vimeo: 'Vimeo',
    dailymotion: 'Dailymotion',
    other: 'la plateforme'
  }
  return names[platform || 'other'] || 'la plateforme'
}

const scrollTo = (id: string) => {
  // Si l'ID est celui d'un module, l'expanser pour afficher toutes ses sections
  const module = filteredModules.value.find(m => m.key === id)
  if (module && !isExpanded(module.key)) {
    toggleExpand(module.key)
    // Attendre un peu que le DOM se mette à jour avant de scroller
    setTimeout(() => {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  } else {
    // Si l'ID est celui d'une section, trouver le module parent et l'expanser
    const parentModule = filteredModules.value.find(m => m.sections.some(s => s.id === id))
    if (parentModule && !isExpanded(parentModule.key)) {
      toggleExpand(parentModule.key)
      setTimeout(() => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } else {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

const router = useRouter()
const go = (to: string) => {
  if (!to) return
  router.push(to)
}

// TOC (sidebar) - utilise les mêmes données filtrées que le contenu pour garantir la cohérence
const tocModules = computed(() => {
  // Utiliser filteredModules pour que le TOC corresponde exactement au contenu affiché
  return filteredModules.value
})

// État d'expansion du TOC (sidebar)
const tocExpanded = ref<Set<string>>(new Set(['introduction','dashboards','paie']))
const tocToggle = (key: string) => {
  const s = new Set(tocExpanded.value)
  if (s.has(key)) s.delete(key); else s.add(key)
  tocExpanded.value = s
}
const tocIsExpanded = (key: string) => tocExpanded.value.has(key)

// Contrôle global d'expansion/réduction du TOC
const allTocExpanded = computed(() => {
  if (tocModules.value.length === 0) return false
  return tocModules.value.every(m => tocExpanded.value.has(m.key))
})

const toggleExpandAll = () => {
  const allExpanded = allTocExpanded.value
  if (allExpanded) {
    // Réduire tout
    tocExpanded.value = new Set()
  } else {
    // Développer tout
    tocExpanded.value = new Set(tocModules.value.map(m => m.key))
  }
}
</script>



